<i18n>
{
    "en": {},
    "ru": {
        "in": "в",
        "today_in": "Сегодня",
        "recommended_to_subscribe": "Рекомендуем подписаться",
        "subscribe": "Подписаться",
        "hide": "Скрыть",
        "subscribe_already": "Вы подписаны",
        "lastday_in": "Вчера"
    }
}
</i18n>
<template>
    <ScrollLoader
        class="feed-list"
        :isLoading="loading"
        :scrollEventOffset="500"
        @onLoad="handleScroll">

        <div class="feed-list__date-group"
            v-for="(feedGroup, index) in computedItems"
            :key="index">

            <div class="feed-list__date-delimiter">
                {{ computedHeaderDate(feedGroup) }}
            </div>

            <div v-for="(feedItem, i) in feedGroup"
                :key="i"
                class="feed-list__item">
                <FeedBlock :open-replies="openReplies" v-bind="feedItem" />

                <PageBlock v-if="showUsersCarousel && feedItem.countIndex === 9"
                    :content-visible="isDeviceDesktop"
                    :small-title="isDeviceDesktop"
                    class="feed-page__block-carousel"
                    :title="$t('recommended_to_subscribe')">

                    <BlockPersonsRow
                        class="feed-page__persons-row"
                        action-buttons
                        :slides-per-view="4"
                        :swiper="isDeviceDesktop"
                        :items="users">
                        <template #item="{ item }" >

                            <BlockPerson
                                is-full-name
                                :user-data="item"
                                without-likes>
                                <div class="feed-page__person-buttons-wrap" slot="footer">

                                    <ButtonBase
                                        @click.native="followUser(item.id)"
                                        :disabled="get(item, 'subscribed', false)"
                                        class="feed-page__person-button">
                                        {{ get(item, 'subscribed', false)
                                            ? $t('subscribe_already')
                                            : $t('subscribe')
                                        }}
                                    </ButtonBase>
                                    <ButtonBase @click.native="hideUser(item.id)"
                                        class="feed-page__person-button"
                                        inverted>
                                        {{ $t('hide') }}
                                    </ButtonBase>

                                </div>
                            </BlockPerson>

                        </template>
                    </BlockPersonsRow>

                </PageBlock>
            </div>

        </div>
    </ScrollLoader>
</template>

<script>
import { mapGetters } from 'vuex'
import throttle from 'lodash/throttle';
import get from 'lodash/get';
import map from 'lodash/map'

import dayjs from 'dayjs';
import PaginatedMixin from '@/mixins/paginated.mixin'

import FeedBlock from '@/components/feed/FeedBlock'
import ScrollLoader from '@/components/ui/ScrollLoader';
import BlockPersonsRow from '@/components/catalog/persons/BlockPersonsRow';
import PageBlock from '@/components/layout/AppPageBlock';
import BlockPerson from '@/components/catalog/persons/BlockPersonCard';
import ButtonBase from '@/components/ui/buttons/ButtonBase'

import Account from '@/models/users/accounts/'
import User from '@/models/users/User'
import Subscription from '@/models/tags/Subscription'

export default {
    name: 'FeedList',
    components: {
        ScrollLoader,
        BlockPerson,
        PageBlock,
        BlockPersonsRow,
        ButtonBase,
        FeedBlock,
    },
    mixins: [ PaginatedMixin ],
    props: {
        users: {
            type: Array,
            default: null
        },
        openReplies: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            // Количество элементов, от которых пользователь отписался
            // Влияет на offset при запросе
            reverseOffset: 0,
            limit: 25
        }
    },
    globalBus: {
        'unsubscribe-feed-block'(config) {
            const { id, type, entity, entityTag } = config

            this.showPopup('subscribe', {
                id,
                type,
                entity,
                entityTag,
                feed: true,
                eventName: 'on-feed-unsubscribe'
            })
        },
        async 'on-feed-unsubscribe'(id, isTag) {
            const account = new Account
            const unsubscribeModel = new Subscription({ id }).for(account)

            try {
                await unsubscribeModel.delete()

                const alertCode = isTag ?
                    'alerts.unsubscribe_tag_success' :
                    'alerts.unsubscribe_success'

                this.filterSubscriptions(id)

                this.$alert.success(this.$t(alertCode))
            } catch(e) {
                this.$alert.error('alerts.error')
            }
        }
    },
    computed: {
        ...mapGetters(['isDeviceDesktop']),
        showUsersCarousel() {
            return this.users && this.users.length
        },
        offset() {
            if (this.items && this.items.length)
                return this.items.length - this.reverseOffset

            return 0
        },
        nextUrl() {
            const apiUrl = get(this.list, '_fromResource')

            if (!apiUrl) return

            return apiUrl + `?offset=${this.offset}&limit=${this.limit}`
        },
        translations(){
            return {
                today_in: this.$t('today_in'),
                lastday_in: this.$t('lastday_in'),
                in: this.$t('in')
            }
        },
        computedItems(){
            const results = [];
            this.items.forEach((item, index) => {
                item.countIndex = index;

                if(this.computeFeedDate(this.items[index - 1], item)){
                    results.push([item])
                }else{
                    results[results.length-1].push(item);
                }
            });

            return results;
        },
        commentsDetailPage(){
            return this.$route.name === 'shopsDetailComments';
        }
    },
    mounted(){
        if(this.canLoad())
            this.toggleFooter('none')
    },
    beforeDestroy(){
        this.toggleFooter('block')
    },
    methods: {
        get,
        canLoad() {
            return (this.items.length < this.totalItemsLength)
        },
        async followUser(id) {
            const subscription = new User({
                user_id: id
            }).for(new Account, new Subscription)

            try {
                const result = await subscription.save()

                if (result.total) {
                    const userIndex = this.users.findIndex(o => o.id === id);
                    this.$set(this.users[userIndex], 'subscribed', true)
                } else {
                    this.$alert.error(this.$t('alerts.error'))
                }
            } catch(e) {
                this.$alert.error(this.$t('alerts.error'))
            }
        },
        hideUser(id) {
            try {
                (new User).hideUserFromRecommended(id)

                const userIndex = this.users.findIndex(o => o.id === id);
                this.$delete(this.users, userIndex)
            } catch(e) {
                this.$alert.error(this.$t('alerts.error'))
            }
        },
        filterSubscriptions(id) {
            // Пересчитываем количество постов, от которых пользователь отписался
            this.reverseOffset = 0

            const filteredItems = map(this.items, feedItem => {
                const subscriptions = map(feedItem.subscriptions, s => {
                    const disabled = s.disabled || (s.id === id)

                    if (disabled && feedItem.subscriptions.length === 1) {
                        this.reverseOffset++
                    }

                    return {
                        ...s,
                        disabled
                    }
                })

                return {
                    ...feedItem,
                    subscriptions
                }
            })

            this.items = filteredItems
        },
        formatedDate(date) {
            return this.$options.filters.datePost(date, this.translations, this.$i18n.locale, 'DD MMMM YYYY', true, true)
        },
        handleScroll: throttle(function(e) {

            if(!this.canLoad())
                this.toggleFooter('block')

            if (this.loading || !this.canLoad()) {
                return false;
            }
            
            this.load(false, this.nextUrl);
        }, 200),
        computeFeedDate(prevFeed, currentFeed) {
            let prevDate;
            let currentDate;
            
            if(this.commentsDetailPage){
                prevDate = get(prevFeed, 'entity.updatedAt', null);
                currentDate = get(currentFeed, 'entity.updatedAt', null);
            }else{
                prevDate = get(prevFeed, 'createdAt', null);
                currentDate = get(currentFeed, 'createdAt', null);
            }

            let result = false;

            if(prevDate && currentDate) {
                result = dayjs(currentDate).isBefore(prevDate, 'day')
            }
            else if(currentDate) {
                result = true;
            }

            return result;
        },
        toggleFooter(style){
            if(!this.isDeviceDesktop)
                document.querySelector('.footer').style.display = style
        },
        computedHeaderDate(feedGroup){
            const date = this.commentsDetailPage ? get(feedGroup, '[0].entity.updatedAt', get(feedGroup, '[0].createdAt')) : get(feedGroup, '[0].createdAt');
            return this.formatedDate(date);
        }
    }
}
</script>

<style lang="less">
.feed-list {

    &__item {
        border-bottom: rem(1) solid @color-gray-white;

        &:last-child{
            border-bottom: none;
        }
    }

    &__date-group{
        position: relative;
    }

    &__date-delimiter {
        position: sticky;
        left: 0;
        top: 0;
        background-color: @color-bg-gray-minor;
        border: rem(1) solid @color-gray-white;
        color: @color-gray-light;
        font-size: rem(13);
        line-height: rem(20);
        text-align: center;
        z-index: 3;
        padding: rem(6) rem(6) rem(4);

        @{mobile} & {
            border-right: none;
            border-left: none;
        }
    }
}
</style>
